import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/table.html
 *
 * Dense: 8px vertical × 12px horizontal row padding, 12px cells, 11px uppercase
 * tracking header. Hover = neutral-50. Selected = indigo-50 tint.
 * `font-variant-numeric: tabular-nums` comes from global styles in theme.
 *
 * v3 port notes:
 * - Slots renamed from v2's `tableAnatomy`: `table` → `root`, `thead` →
 *   `header`, `tbody` → `body`, `tr` → `row`, `th` → `columnHeader`,
 *   `td` → `cell`. v3 adds `footer`/`caption`, both unused here (no
 *   MTfoot/MTableCaption call site anywhere in the app).
 * - v2's `baseStyle` applied unconditionally regardless of which variant was
 *   selected (chakra v2 always applies `baseStyle` before layering the
 *   active variant on top) — ported as this recipe's own `base`, so
 *   `preview`/`border`/`noHover` only need to express their *differences*
 *   from it, matching the original file's structure exactly (v2's
 *   `noHoverStyle` was spread from `baseStyle` with one property changed;
 *   `preview`/`border` fully replaced the header/row/cell look).
 * - Cell styles sit on nested `& th`/`& td` selectors under the `header`/
 *   `body` slots, exactly as v2 nested them under `thead`/`tbody`. That
 *   nesting is (0,1,1), so it out-ranks both the built-in `size` variant's
 *   `columnHeader`/`cell` padding and any call-site style prop on a single
 *   `Th`/`Td` (one class, (0,1,0)). In v2 that made every table 8px × 12px
 *   whatever its `size`, and left call-site `px`/`fontWeight`/`textTransform`
 *   on cells dead; moving the styles onto the `columnHeader`/`cell` slots
 *   let `size="sm"` shrink cells to 8px and let those dead props through.
 *   Only `!important` call-site styles (DataTableNew's header) still win.
 * - `size` carries just what v2's built-in sizes still contributed under that
 *   nesting: the `th`/`td` line heights (1rem headers; 1rem/1.25rem/1.5rem
 *   cells at sm/md/lg), which beat the built-in root `textStyle`'s 20px.
 * - A handful of real call sites pass v2-stock variant names this app's own
 *   theme never defined even in v2 (`variant="simple"`, `variant="unstyled"`)
 *   — `@chakra-ui/theme`'s v2 stock Table variants aren't inspectable
 *   anymore (the package is gone from the dependency tree entirely, same
 *   gap noted in Tag.ts), so these now render as bare `base` with no
 *   variant-specific styling in v3 rather than v2's stock "simple"/
 *   "unstyled" look. Not a regression this port can fix — there's nothing
 *   left to port from.
 */

const baseColumnHeader = {
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 'wider', // 0.08em
  fontSize: 'xs', // 11px
  color: 'fg.muted',
  // Intentionally lighter than `border.subtle`. Header borders are a visual
  // hint to separate filter/sort icons from neighbors, not a hard rule.
  borderBottom: '1px solid rgba(7, 7, 35, 0.05)',
  py: '8px',
  px: '12px',
  borderLeft: '1px solid rgba(7, 7, 35, 0.05)',
  _first: { borderLeft: 'none' },
  '&[data-is-numeric=true]': {
    textAlign: 'end',
  },
};

const baseCell = {
  fontSize: 'sm', // 12px
  color: 'fg.primary',
  borderColor: 'border.subtle',
  py: '8px',
  px: '12px',
  '&[data-is-numeric=true]': {
    textAlign: 'end',
  },
};

const baseRow = {
  borderBottom: '1px solid',
  borderColor: 'border.subtle',
  _last: { borderBottom: 'none' },
  _selected: { backgroundColor: 'bg.selected' },
  _hover: { backgroundColor: 'bg.subtle' },
};

export const tableTheme = defineSlotRecipe({
  slots: [
    'root',
    'header',
    'body',
    'row',
    'columnHeader',
    'cell',
    'footer',
    'caption',
  ],
  base: {
    header: { '& th': baseColumnHeader },
    body: { '& td': baseCell },
    row: baseRow,
  },
  variants: {
    variant: {
      // v2's `default` variant was just `baseStyle`, which is this recipe's
      // `base`. Declared (but empty) so `defaultVariants` below can select it;
      // selecting it also keeps the built-in `line` variant from applying.
      default: {},
      preview: {
        root: {
          background: 'bg.muted',
          borderRadius: 'sm',
        },
        header: {
          '& th': {
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 'wider',
            fontSize: '10px',
            color: 'fg.muted',
            padding: 1,
            borderBottom: 'none',
            borderLeft: 'none',
            '&[data-is-numeric=true]': {
              textAlign: 'end',
            },
          },
        },
        body: {
          '& td': {
            fontWeight: 'normal',
            fontSize: '9px',
            color: 'fg.primary',
            padding: 1,
            borderColor: 'transparent',
            '&[data-is-numeric=true]': {
              textAlign: 'end',
            },
          },
        },
        row: {
          borderBottom: '1px solid transparent',
          borderColor: 'border.subtle',
          _hover: { backgroundColor: 'transparent' },
          _selected: { backgroundColor: 'transparent' },
        },
      },
      noHover: {
        row: {
          _hover: { backgroundColor: 'transparent' },
        },
      },
      border: {
        header: {
          '& th': {
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 'wider',
            fontSize: 'xs',
            color: 'fg.muted',
            borderColor: 'border.subtle',
            borderBottom: '1px solid',
            borderLeft: 'none',
            p: '8px',
            '&[data-is-numeric=true]': {
              textAlign: 'end',
            },
          },
        },
        row: {
          borderBottom: '1px solid',
          borderColor: 'border.subtle',
          _selected: { backgroundColor: 'bg.selected' },
          _hover: { backgroundColor: 'bg.subtle' },
        },
        // Un-nested, as in v2: its padding loses to the base `& td`'s
        // 8px × 12px, which is what v2 rendered for these cells.
        cell: {
          fontWeight: 400,
          fontSize: 'sm',
          color: 'fg.primary',
          borderColor: 'border.subtle',
          p: '8px',
          '&[data-is-numeric=true]': {
            textAlign: 'end',
          },
        },
      },
    },
    size: {
      sm: {
        columnHeader: { lineHeight: '1rem' },
        cell: { lineHeight: '1rem' },
      },
      md: {
        columnHeader: { lineHeight: '1rem' },
        cell: { lineHeight: '1.25rem' },
      },
      lg: {
        columnHeader: { lineHeight: '1.25rem' },
        cell: { lineHeight: '1.5rem' },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export default tableTheme;
